import { useParams, Link } from "wouter";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { useGetService } from "@workspace/api-client";
import { SEO } from "@/components/SEO";
import { JsonLd } from "@/components/JsonLd";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { data: service, isLoading } = useGetService(slug || "", { query: { enabled: !!slug } as any });

  if (isLoading) {
    return <div className="pt-32 pb-20 px-4 text-center text-muted-foreground">Loading...</div>;
  }

  if (!service) {
    return (
      <div className="pt-32 pb-20 px-4 text-center">
        <h1 className="font-display text-4xl font-bold mb-4">Service Not Found</h1>
        <p className="text-muted-foreground mb-8">This service doesn't exist or has been removed.</p>
        <Link href="/services" className="inline-flex items-center text-primary hover:underline">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Services
        </Link>
      </div>
    );
  }

  const longDesc = service.longDesc || "";
  const description = service.description;
  const plainDescription = longDesc
    ? longDesc.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()
    : description;
  const features = service.features ? service.features.split(',') : [];

  return (
    <div className="flex flex-col w-full">
      <SEO
        title={`${service.title} | Service`}
        description={plainDescription.substring(0, 160)}
      />
      <JsonLd schema={[
        {
          "@context": "https://schema.org",
          "@type": "Service",
          "name": service.title,
          "url": `https://acceleratebyte.com/services/${slug}`,
          "description": plainDescription.substring(0, 300),
          "provider": { "@id": "https://acceleratebyte.com/#organization" },
          "areaServed": [
            { "@type": "Country", "name": "India" },
            { "@type": "Country", "name": "United Kingdom" },
            { "@type": "Country", "name": "United States" },
            { "@type": "Country", "name": "United Arab Emirates" }
          ],
          "serviceType": "Software Development"
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://acceleratebyte.com" },
            { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://acceleratebyte.com/services" },
            { "@type": "ListItem", "position": 3, "name": service.title, "item": `https://acceleratebyte.com/services/${slug}` }
          ]
        }
      ]} />

      {/* Hero */}
      <section className="pt-32 pb-20 border-b border-border/50">
        <div className="container mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <Link href="/services" className="inline-flex items-center text-sm font-medium text-muted-foreground hover:text-foreground mb-10 transition-colors">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Services
          </Link>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-display text-4xl md:text-6xl font-bold mb-8"
          >
            {service.title}
          </motion.h1>
          {longDesc ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="prose prose-invert prose-lg max-w-none text-muted-foreground [&_p]:mb-5 [&_p]:leading-relaxed [&_p]:break-words"
              dangerouslySetInnerHTML={{ __html: longDesc }}
            />
          ) : (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-muted-foreground leading-relaxed"
            >
              {description}
            </motion.p>
          )}
        </div>
      </section>

      {/* Features + CTA */}
      <section className="py-20">
        <div className="container mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
            {features.length > 0 && (
              <div>
                <h2 className="font-display text-3xl font-bold mb-8">What's Included</h2>
                <ul className="space-y-4">
                  {features.map((feature: string, i: number) => (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1 }}
                      className="flex items-start gap-3"
                    >
                      <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-0.5" />
                      <span className="text-lg text-muted-foreground">{feature.trim()}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            )}
            <div className={`bg-card border border-border/50 rounded-2xl p-8 flex flex-col justify-center text-center ${features.length === 0 ? "md:col-span-2 md:max-w-md md:mx-auto" : ""}`}>
              <h3 className="font-display text-2xl font-bold mb-4">Ready to accelerate?</h3>
              <p className="text-muted-foreground mb-8">Let's discuss how we can apply this service to your specific business needs.</p>
              <Link href="/contact" className="inline-flex h-12 items-center justify-center rounded-md bg-primary px-8 text-base font-medium text-primary-foreground shadow transition-colors hover:bg-primary/90">
                Start Your Project
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
